import { useQuery } from '@apollo/client/react';
import { ScrollPage } from '@optools/components/layout/layout';
import { PageLoader } from '@optools/components/loading/page-loader';
import { Input } from '@optools/components/ui/input';
import { GET_ZIXI_SOURCES_BY_TEAM } from '@optools/services/operator-gateway/btc/queries';
import { ZixiPipelineConfig } from '@optools/services/operator-gateway/btc/types';
import { GET_PIPELINE_CONFIGS } from '@optools/services/operator-gateway/pipeline-config/queries';
import { Region } from '@optools/services/operator-gateway/types/generated/graphql';
import { ZixiSourcesCard } from '@optools/tools/btc-tools/components/zixi-sources-card';
import { filterZixiPipelineConfigsByTerm } from '@optools/tools/btc-tools/utils/filter';
import { authorizedRouteLoader } from '@optools/utils/auth/authorized-route-loader';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';

export const Route = createFileRoute('/_auth/btc-tools/team-zixi-sources')({
  beforeLoad: authorizedRouteLoader('btcTools.teamZixiSources'),
  component: RouteComponent,
});

export function RouteComponent() {
  const [term, setTerm] = useState('');
  const { data, loading } = useQuery(GET_PIPELINE_CONFIGS);
  const { data: zixiData, loading: loadingZixiData } = useQuery(GET_ZIXI_SOURCES_BY_TEAM);

  // This change is a side effect of the updates to support multi-region pipeline
  // configs. Until a long term solution is found we only need to work about configs in
  // the EAST region.
  const teamSources: ZixiPipelineConfig[] | undefined = data?.pipelineConfigs
    ?.filter((config) => config.region === Region.EAST)
    .map((config) => {
      const sources = zixiData?.searchZixiSourcesByTeam?.filter(
        (source) => source.teamId === config.teamId,
      );

      return {
        ...config,
        zixiSources: sources,
      };
    });

  const filteredConfigs = filterZixiPipelineConfigsByTerm(teamSources, term);

  const content = (() => {
    if (loading || loadingZixiData) {
      return <PageLoader />;
    } else if (!filteredConfigs?.length) {
      return (
        <div className="flex h-full items-center justify-center">
          <span className="text-lg font-bold">No Pipeline Configs Found</span>
        </div>
      );
    } else {
      return (
        <ScrollPage className="flex flex-wrap content-start gap-4">
          {filteredConfigs?.map((item) => {
            return (
              <ZixiSourcesCard
                key={item.id}
                partnerLockEnabled={item.partnerLockEnabled}
                partnerLockExclusions={item.partnerLockExclusions}
                sources={item.zixiSources}
                teamId={item.teamId}
              />
            );
          })}
        </ScrollPage>
      );
    }
  })();

  return (
    <div className="grid h-full grid-rows-[auto_1fr] gap-4">
      <Input
        aria-label="Search Pipeline Configs"
        className="w-sm"
        defaultValue=""
        placeholder="Search"
        onChange={(event) => setTerm(event.target.value)}
      />
      {content}
    </div>
  );
}
